import salesService from '../services/salesService';
import { toast } from 'react-hot-toast';

/**
 * Check if running in Electron desktop app
 * @returns {boolean}
 */
export const isDesktopApp = () => {
  return typeof window !== 'undefined' && !!window.electronAPI;
};

/**
 * Format phone number for WhatsApp
 * WhatsApp requires: country code + number (without + sign in URL)
 * Example: "+91 9600 635 244" -> "919600635244"
 * Example: "9600635211" -> "919600635211" (adds India country code 91)
 * Example: "09600635211" -> "919600635211" (removes leading 0, adds country code)
 * 
 * @param {string} phone - Phone number to format
 * @returns {string|null} - Formatted phone number or null if invalid
 */
export const formatPhoneNumberForWhatsApp = (phone) => {
  if (!phone || typeof phone !== 'string') {
    return null;
  }
  
  // Remove all non-digit characters except +
  let formatted = phone.replace(/[^\d+]/g, '').trim();
  
  if (formatted.length === 0) {
    return null;
  }
  
  // Remove + sign if present (WhatsApp URL doesn't need it)
  formatted = formatted.replace(/\+/g, '');
  
  // If empty after removing +, return null
  if (formatted.length === 0) {
    return null;
  }
  
  // Validate: should only contain digits
  if (!/^\d+$/.test(formatted)) {
    console.warn('Phone number contains non-digits:', formatted);
    return null;
  }
  
  // Handle Indian phone numbers (most common case)
  // If number starts with 0, remove it (local format)
  if (formatted.startsWith('0')) {
    formatted = formatted.substring(1);
  }
  
  // If number is 10 digits and doesn't start with country code, assume it's Indian and add 91
  if (formatted.length === 10 && !formatted.startsWith('91')) {
    formatted = '91' + formatted;
  }
  
  // If number is 11 digits and starts with 0, remove 0 and add 91
  if (formatted.length === 11 && formatted.startsWith('0')) {
    formatted = '91' + formatted.substring(1);
  }
  
  // Validate: should be at least 10 digits (after formatting)
  if (formatted.length < 10) {
    console.warn('Phone number too short after formatting:', formatted);
    return null;
  }
  
  // Validate: should not be too long (max 15 digits for international numbers)
  if (formatted.length > 15) {
    console.warn('Phone number too long:', formatted);
    return null;
  }
  
  console.log('Original phone:', phone, '-> Formatted for WhatsApp:', formatted);
  return formatted;
};

/**
 * Share bill to WhatsApp
 * Generates PDF receipt and opens WhatsApp with the bill
 * 
 * @param {number|string} orderId - Sales order ID
 * @param {Object} customer - Customer object with phone property (optional)
 * @param {string} orderNumber - Order number for the message (optional)
 * @returns {Promise<boolean>} - Returns true if successful, false otherwise
 */
export const shareBillToWhatsApp = async (orderId, customer = null, orderNumber = null) => {
  try {
    // Show loading toast
    const loadingToast = toast.loading('Generating bill...');
    
    // Generate PDF receipt
    const receiptBlob = await salesService.generateReceipt(orderId, 'CUSTOMER', null);
    
    // Dismiss loading toast
    toast.dismiss(loadingToast);
    
    if (!receiptBlob) {
      toast.error('Failed to generate receipt');
      return false;
    }
    
    const fileName = `Invoice-${orderNumber || orderId}.pdf`;
    const file = new File([receiptBlob], fileName, { type: 'application/pdf' });
    const customerPhone = customer?.phone ? formatPhoneNumberForWhatsApp(customer.phone) : null;
    const messageText = `Invoice ${orderNumber || orderId}`;
    const message = encodeURIComponent(messageText);

    // Try Web Share API first (works best on mobile and some desktop browsers)
    if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
      try {
        // If customer phone is available, open WhatsApp chat first
        if (customerPhone) {
          const whatsappUrl = `https://wa.me/${customerPhone}?text=${message}`;
          window.open(whatsappUrl, '_blank');
          // Wait a bit for WhatsApp to open
          await new Promise((r) => setTimeout(r, 1000));
        }
        
        // Share the file using Web Share API
        await navigator.share({
          title: messageText,
          text: messageText,
          files: [file]
        });
        
        toast.success('Bill shared successfully!');
        return true;
      } catch (shareError) {
        // If share fails, continue with fallback method
        console.log('Web Share API failed, falling back to download method:', shareError);
      }
    }
    
    // Fallback: Download PDF and open WhatsApp
    // Create download link
    const blobUrl = window.URL.createObjectURL(receiptBlob);
    const downloadLink = document.createElement('a');
    downloadLink.href = blobUrl;
    downloadLink.download = fileName;
    downloadLink.style.display = 'none';
    document.body.appendChild(downloadLink);
    
    // Download the PDF
    downloadLink.click();
    
    // Clean up after a short delay
    setTimeout(() => {
      document.body.removeChild(downloadLink);
      window.URL.revokeObjectURL(blobUrl);
    }, 100);
    
    // Copy message to clipboard for easy pasting
    try {
      await navigator.clipboard.writeText(messageText);
    } catch (err) {
      console.log('Could not copy to clipboard:', err);
    }
    
    // Open WhatsApp with customer's chat if phone number is available
    let whatsappUrl;
    if (customerPhone) {
      // Open WhatsApp directly to customer's chat
      whatsappUrl = `https://wa.me/${customerPhone}?text=${message}`;
      toast.success(`Opening WhatsApp chat for customer. PDF downloaded - please attach it.`, {
        duration: 5000,
      });
    } else {
      // Open WhatsApp Web for user to search manually
      whatsappUrl = `https://web.whatsapp.com/send?text=${message}`;
      toast.success('PDF downloaded. Opening WhatsApp - please attach the PDF file.', {
        duration: 5000,
      });
    }
    
    // Open WhatsApp in new tab/window
    window.open(whatsappUrl, '_blank');
    
    return true;
  } catch (error) {
    console.error('Error sharing bill to WhatsApp:', error);
    toast.error('Failed to share bill to WhatsApp. Please try again.');
    return false;
  }
};

/**
 * Share bill to WhatsApp (alternative method using WhatsApp Web API)
 * This method tries to use WhatsApp Web's share feature
 * 
 * @param {number|string} orderId - Sales order ID
 * @param {Object} customer - Customer object with phone property (optional)
 * @param {string} orderNumber - Order number for the message (optional)
 * @returns {Promise<boolean>} - Returns true if successful, false otherwise
 */
export const shareBillToWhatsAppWeb = async (orderId, customer = null, orderNumber = null) => {
  try {
    return await shareBillToWhatsApp(orderId, customer, orderNumber);
  } catch (error) {
    console.error('Error sharing bill to WhatsApp Web:', error);
    toast.error('Failed to share bill to WhatsApp. Please try again.');
    return false;
  }
};

/**
 * Convert blob to base64 string
 * @param {Blob} blob 
 * @returns {Promise<string>}
 */
const blobToBase64 = (blob) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const base64 = reader.result.split(',')[1];
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
};

/**
 * Share bill to WhatsApp using Desktop App (Electron only) - Approach B Auto-Send
 * Automatically sends bill as PDF document to customer's WhatsApp
 * 
 * @param {number|string} orderId - Sales order ID
 * @param {Object} customer - Customer object with phone property (optional)
 * @param {string} orderNumber - Order number for the message (optional)
 * @returns {Promise<boolean>} - Returns true if successful, false otherwise
 */
export const shareBillToWhatsAppDesktop = async (orderId, customer = null, orderNumber = null) => {
  if (!isDesktopApp()) {
    console.warn('shareBillToWhatsAppDesktop called in non-desktop environment');
    return shareBillToWhatsApp(orderId, customer, orderNumber);
  }

  const customerPhone = customer?.phone ? formatPhoneNumberForWhatsApp(customer.phone) : null;
  
  // Check if customer has a valid phone number
  if (!customerPhone) {
    toast.error('Customer does not have a valid phone number for WhatsApp');
    return false;
  }

  try {
    // First check if WhatsApp is connected
    const status = await window.electronAPI.getWhatsAppStatus();
    if (!status.ready) {
      toast.error('WhatsApp is not connected. Please go to Settings → WhatsApp Configuration to connect.', {
        duration: 5000,
      });
      return false;
    }

    const loadingToast = toast.loading('Sending bill to WhatsApp...');
    
    // Generate PDF receipt
    const receiptBlob = await salesService.generateReceipt(orderId, 'CUSTOMER', null);
    
    if (!receiptBlob) {
      toast.dismiss(loadingToast);
      toast.error('Failed to generate receipt');
      return false;
    }
    
    // Convert PDF to base64
    const pdfBase64 = await blobToBase64(receiptBlob);
    const fileName = `Invoice-${orderNumber || orderId}.pdf`;
    const messageText = `Invoice ${orderNumber || orderId}`;
    
    // Send via WhatsApp
    const result = await window.electronAPI.sendWhatsAppMessage({
      phone: customerPhone,
      message: messageText,
      mediaBase64: pdfBase64,
      mimeType: 'application/pdf',
      filename: fileName
    });
    
    toast.dismiss(loadingToast);
    
    if (result.success) {
      toast.success(`Bill sent to WhatsApp successfully!`, {
        duration: 3000,
      });
      return true;
    } else {
      toast.error(result.error || 'Failed to send bill to WhatsApp');
      return false;
    }
  } catch (error) {
    console.error('Error sending bill to WhatsApp (Desktop):', error);
    toast.error('Failed to send bill to WhatsApp. Please try again.');
    return false;
  }
};

/**
 * Initialize WhatsApp connection (Desktop only - Approach B)
 * @returns {Promise<boolean>}
 */
export const initializeWhatsApp = async () => {
  if (!isDesktopApp()) {
    window.open('https://web.whatsapp.com', '_blank');
    return true;
  }
  
  try {
    await window.electronAPI.initializeWhatsApp();
    return true;
  } catch (error) {
    console.error('Error initializing WhatsApp:', error);
    return false;
  }
};

/**
 * Get WhatsApp status (Desktop only - Approach B)
 * @returns {Promise<{initialized: boolean, ready: boolean, authenticated: boolean, qrCode: string|null, error: string|null}>}
 */
export const getWhatsAppStatus = async () => {
  if (!isDesktopApp()) {
    return { initialized: false, ready: false, authenticated: false, qrCode: null, error: null };
  }
  
  try {
    return await window.electronAPI.getWhatsAppStatus();
  } catch (error) {
    console.error('Error getting WhatsApp status:', error);
    return { initialized: false, ready: false, authenticated: false, qrCode: null, error: error.message };
  }
};

/**
 * Check if phone number is registered on WhatsApp (Desktop only - Approach B)
 * @param {string} phone 
 * @returns {Promise<{registered: boolean, error?: string}>}
 */
export const isWhatsAppRegistered = async (phone) => {
  if (!isDesktopApp()) {
    return { registered: false, error: 'Not a desktop app' };
  }
  
  try {
    return await window.electronAPI.isWhatsAppRegistered(phone);
  } catch (error) {
    console.error('Error checking WhatsApp registration:', error);
    return { registered: false, error: error.message };
  }
};

/**
 * Logout from WhatsApp (Desktop only - clears session)
 * @returns {Promise<{success: boolean}>}
 */
export const logoutWhatsApp = async () => {
  if (!isDesktopApp()) {
    return { success: false, error: 'Not a desktop app' };
  }
  
  try {
    const result = await window.electronAPI.logoutWhatsApp();
    if (result.success) {
      toast.success('WhatsApp session cleared. You will need to scan QR code again.');
    }
    return result;
  } catch (error) {
    console.error('Error logging out of WhatsApp:', error);
    toast.error('Failed to logout from WhatsApp');
    return { success: false, error: error.message };
  }
};

/**
 * Smart share - uses desktop flow if in Electron, otherwise uses web flow
 * @param {number|string} orderId - Sales order ID
 * @param {Object} customer - Customer object with phone property (optional)
 * @param {string} orderNumber - Order number for the message (optional)
 * @returns {Promise<boolean>}
 */
export const smartShareBillToWhatsApp = async (orderId, customer = null, orderNumber = null) => {
  if (isDesktopApp()) {
    return shareBillToWhatsAppDesktop(orderId, customer, orderNumber);
  }
  return shareBillToWhatsApp(orderId, customer, orderNumber);
};

const sanitizeOneLine = (value) => String(value ?? '').replace(/[\r\n]+/g, ' ').trim();

/**
 * Plain-text payment reminder (manual desktop feature; not bill PDF flow).
 * Uses WhatsApp *bold* markers for company name and amount.
 *
 * @param {Object} params
 * @param {string} params.customerName
 * @param {string} params.balanceFormatted e.g. "₹787.50"
 * @param {string} params.companyName
 * @param {string} [params.companyContact] business phone for footer (digits or formatted)
 */
export const buildPaymentReminderMessage = ({
  customerName,
  balanceFormatted,
  companyName,
  companyContact,
}) => {
  const cust = sanitizeOneLine(customerName) || 'Customer';
  const co = sanitizeOneLine(companyName) || 'Our business';
  const amtRaw = balanceFormatted != null ? String(balanceFormatted).trim() : '';
  const amt = amtRaw || 'your account';
  const contact = sanitizeOneLine(companyContact);

  const lines = [
    `Dear ${cust},`,
    '',
    `Greetings from *${co}*.`,
    '',
    `This is a gentle reminder that your outstanding balance is *${amt}*.`,
    '',
    'We kindly request you to make the payment at your earliest convenience. If payment has already been made, please ignore this message.',
    '',
    'Thank you for choosing our services.',
    '',
    'Regards,',
    `*${co}*`,
  ];
  if (contact) {
    lines.push(`📞 ${contact}`);
  }
  return lines.join('\n');
};

/**
 * Send a single payment reminder via desktop WhatsApp session (text only).
 * Caller enforces daily quota and usagePaymentReminder flag.
 *
 * @param {Object} customer — must include `phone`, `name`
 * @param {string} balanceFormatted — pre-formatted currency string
 * @param {{ companyName: string, companyContact?: string }} [companyMeta] — from company settings + session
 * @returns {Promise<{ success: boolean, error?: string }>}
 */
export const sendPaymentReminderForCustomerDesktop = async (customer, balanceFormatted, companyMeta = {}) => {
  if (!isDesktopApp() || !window.electronAPI?.sendWhatsAppMessage) {
    return { success: false, error: 'WhatsApp reminders are only available in the desktop app.' };
  }

  const waPhone = customer?.phone != null ? formatPhoneNumberForWhatsApp(String(customer.phone)) : null;
  if (!waPhone) {
    return { success: false, error: 'Invalid or missing phone number for WhatsApp.' };
  }

  try {
    const status = await window.electronAPI.getWhatsAppStatus();
    if (!status.ready) {
      return {
        success: false,
        error: 'WhatsApp is not connected. Open Settings → WhatsApp Configuration and scan the QR code.',
      };
    }

    const message = buildPaymentReminderMessage({
      customerName: customer.name,
      balanceFormatted,
      companyName: companyMeta.companyName,
      companyContact: companyMeta.companyContact,
    });
    const result = await window.electronAPI.sendWhatsAppMessage({
      phone: waPhone,
      message,
    });

    if (result.success) {
      return { success: true };
    }
    return { success: false, error: result.error || 'Failed to send WhatsApp message.' };
  } catch (error) {
    console.error('sendPaymentReminderForCustomerDesktop:', error);
    return { success: false, error: error.message || 'Failed to send WhatsApp message.' };
  }
};

/**
 * Send reminders in sequence with delay (reduces ban risk).
 * @param {Array<{ customer: object, balanceFormatted: string }>} items
 * @param {{ delayMs?: number, companyMeta?: { companyName: string, companyContact?: string }, onProgress?: (info: { index: number, total: number, customer: object, result: object }) => void }} options
 */
export const sendPaymentRemindersSequentialDesktop = async (items, options = {}) => {
  const delayMs = options.delayMs != null ? options.delayMs : 1500;
  const companyMeta = options.companyMeta || {};
  const results = [];
  for (let i = 0; i < items.length; i += 1) {
    const { customer, balanceFormatted } = items[i];
    const result = await sendPaymentReminderForCustomerDesktop(customer, balanceFormatted, companyMeta);
    results.push({ customer, result });
    if (options.onProgress) {
      options.onProgress({ index: i, total: items.length, customer, result });
    }
    if (i < items.length - 1 && delayMs > 0) {
      await new Promise((r) => setTimeout(r, delayMs));
    }
  }
  return results;
};

export default {
  isDesktopApp,
  formatPhoneNumberForWhatsApp,
  shareBillToWhatsApp,
  shareBillToWhatsAppWeb,
  shareBillToWhatsAppDesktop,
  smartShareBillToWhatsApp,
  initializeWhatsApp,
  getWhatsAppStatus,
  isWhatsAppRegistered,
  logoutWhatsApp,
  buildPaymentReminderMessage,
  sendPaymentReminderForCustomerDesktop,
  sendPaymentRemindersSequentialDesktop,
};
